import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { UPDATE_CONVERSATION_MESSAGE_MUTATION } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { ConversationMessageRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';

export const useConversationMessageUpdate = () => {
  const { showErrorAlert } = useNotifications();

  const [updateConversationMessageMutation, { loading }] = useMutation(
    UPDATE_CONVERSATION_MESSAGE_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating conversation message, please try again');
      },
    },
  );

  const updateMessage = useCallback(
    (message: { conversationMessageId: string; body: string }) => {
      return updateConversationMessageMutation({
        variables: {
          conversationMessageId: message.conversationMessageId,
          body: message.body,
        },
        update: (
          cache,
          {
            data: response,
          }: {
            data?: {
              updateConversationMessage: Partial<ConversationMessageRaw[]>;
            };
          },
        ) => {
          if (!response?.updateConversationMessage?.[0]) {
            return;
          }

          const messageId = response.updateConversationMessage[0].conversationMessageId;
          // find message in cache by conversationMessageId
          const messageToUpdate = cache.identify({
            __typename: 'ConversationMessage',
            id: messageId,
          });

          const fields = generateFieldModifiersMap({
            body: message.body,
          });

          cache.modify({
            id: messageToUpdate,
            fields,
          });
        },
      });
    },
    [updateConversationMessageMutation],
  );

  return {
    loading,
    updateMessage,
  };
};
